import React, { useState } from 'react';
import { Box, IconButton, Typography, Collapse, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import AssignedReviewTeam from './AssignedReviewTeam';
import { spacingTokens } from '../../../theme/spacingTokens';
import { typographyTokens, typography } from '../../../theme/typographyTokens';

interface CollapsibleAssignedReviewTeamProps {
  sepID: number;
  defaultExpanded?: boolean;
}

const CollapsibleAssignedReviewTeam: React.FC<CollapsibleAssignedReviewTeamProps> = ({
  sepID,
  defaultExpanded = true,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

  const toggle = () => setExpanded((prev) => !prev);

  return (
    <Box
      sx={{
        // slightly lower and shifted left to sit under KnockoutDetails
        my: { xs: spacingTokens.sm, md: spacingTokens.md },
        width: '100%',
        // Reduce large-breakpoint horizontal padding so the box aligns a bit more left
        px: { xs: spacingTokens.md, sm: spacingTokens.lg, md: '40px', lg: '40px', xl: '40px' },
      }}
    >
      {/* Header row: title + arrow toggle, aligned with SEP name */}
      <Box
        onClick={toggle}
        role="button"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse Assigned Review Team' : 'Expand Assigned Review Team'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacingTokens.xs,
          cursor: 'pointer',
          padding: `${spacingTokens.sm} 0`,
          userSelect: 'none',
          '&:hover': {
            '& .review-team-title': {
              color: theme.palette.solidBlue.main,
            },
          },
        }}
      >
        <Typography
          className="review-team-title"
          sx={{
            fontSize: typography.body2.fontSize,
            fontWeight: typographyTokens.fontWeights.bold,
            textTransform: 'uppercase',
            letterSpacing: typography.button.letterSpacing,
            color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default,
            transition: 'color 0.2s ease-in-out',
            ml: { xs: 0, sm: 0, md: spacingTokens.lg },
          }}
        >
          Assigned Review Team
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          sx={{
            color: theme.palette.solidBlue.main,
            padding: '2px',
          }}
        >
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        {/* Existing component stays untouched */}
        <AssignedReviewTeam sepID={sepID} />
      </Collapse>
    </Box>
  );
};

export default CollapsibleAssignedReviewTeam;
